import React, { useState } from "react";
import { X } from "lucide-react";
import { accountErr } from "../../api/account/accountApi";

interface CodeModalProps {
  title: string;
  description: string;
  onVerify: (code: string) => Promise<void>;
  onResend?: () => Promise<void>;
  onClose: () => void;
  verifyLabel?: string;
}

/** One-time-code prompt shared by email / phone verification. */
const CodeModal: React.FC<CodeModalProps> = ({
  title,
  description,
  onVerify,
  onResend,
  onClose,
  verifyLabel = "Verify",
}) => {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [resent, setResent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (code.trim().length < 4) {
      setError("Enter the code you received");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await onVerify(code.trim());
    } catch (err) {
      setError(accountErr(err, "That code was not accepted"));
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    if (!onResend) return;
    setBusy(true);
    setError("");
    try {
      await onResend();
      setResent(true);
    } catch (err) {
      setError(accountErr(err, "Could not resend the code"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="acct-modal-overlay" onClick={onClose}>
      <form
        className="acct-modal"
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
      >
        <div className="acct-modal__head">
          <h3>{title}</h3>
          <button type="button" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>
        <p>{description}</p>
        <input
          className="code-input"
          autoFocus
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={12}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\s/g, ""))}
          placeholder="••••••"
        />
        {error && <p className="acct-form-error">{error}</p>}
        <div className="acct-actions">
          {onResend && (
            <button
              type="button"
              className="acct-link"
              onClick={resend}
              disabled={busy}
              style={{ marginRight: "auto" }}
            >
              {resent ? "Code re-sent" : "Resend code"}
            </button>
          )}
          <button type="button" className="acct-btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            className="acct-btn acct-btn--primary"
            disabled={busy}
          >
            {busy ? "Checking..." : verifyLabel}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CodeModal;
